import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { designAPI } from '@api/designs';
import { setCoverages, setManufacturers, setModels } from '@features/redux/slices/designSlice';
import { store } from '@features/redux/store';
import { useAlert } from './useAlert';

import type { RootState } from '@features/redux/store';

export const useLoadDesignData = () => {
    const { IdToken } = useSelector((state: RootState) => state.auth);
    const { setAlert } = useAlert();

    const { manufacturers, models, coverages } = useSelector((state: RootState) => state.design);

    const [loadingCoverage, setLoadingCoverage] = useState<boolean>(false);
    const [loadingModel, setLoadingModel] = useState<boolean>(false);
    const [loadingManufacturer, setLoadingManufacturer] = useState<boolean>(false);

    const { getCoverages, getModels, getManufacturers } = designAPI(IdToken as string);

    const loadManufacturers = async (force?: boolean) => {
        try {
            if (!manufacturers.length || force) {
                setLoadingManufacturer(true);
                const manufacturers = (await getManufacturers()) || [];
                store.dispatch(setManufacturers(manufacturers));
            }
        } catch (error) {
            setAlert('Failed to load manufacturers. Please try again.', 'error');
        } finally {
            setLoadingManufacturer(false);
        }
    };

    const loadCoverages = async (force?: boolean) => {
        try {
            if (!coverages.length || force) {
                setLoadingCoverage(true);
                const coverages = (await getCoverages('eng')) || [];
                store.dispatch(setCoverages(coverages));
            }
        } catch (error) {
            setAlert('Failed to load coverages. Please try again.', 'error');
        } finally {
            setLoadingCoverage(false);
        }
    };

    const loadModels = async (force?: boolean) => {
        try {
            if (!models.length || force) {
                setLoadingModel(true);
                const models = (await getModels()) || [];
                store.dispatch(setModels(models));
            }
        } catch (error) {
            setAlert('Failed to load models. Please try again.', 'error');
        } finally {
            setLoadingModel(false);
        }
    };

    useEffect(() => {
        loadManufacturers();
        loadCoverages();
        loadModels();
    }, []);

    return {
        manufacturers,
        models,
        coverages,
        loadCoverages,
        loadModels,
        loadManufacturers,
        loadingCoverage,
        loadingManufacturer,
        loadingModel,
    };
};
